// graphLayout.ts — the git-graph's geometry, extracted from RepoGraphView so
// the renderer stays a renderer. Nothing here touches React or the DOM.
//
// Lane layout: chronological assignment, but a new branch's lane is placed
// immediately adjacent to its parent commit's lane (alternating left/right
// per encounter) so fork connectors don't have to cross unrelated trunks.
//
// Row layout: rows are packed onto a grid of ROW_STEP slots. The only
// ordering rule is the commit graph — a commit sits below every visible
// descendant — so unrelated branches stack side by side instead of each
// commit taking its own timeline row. Each commit takes the highest slot
// its descendants allow where its dot + button don't collide with a row
// already placed (packRows).
//
// Branch labels live in a header band above the newest row, each at the x
// of its branch's newest visible commit and joined to it by a dotted line.
// Branches whose tips are the same commit stack their pills in one block;
// blocks that would overlap horizontally rise into higher tiers.
import { colorForHue, hueOf, preferWhiteFg } from './colors';
import { formatNum } from './format';
import type { ThemeMode } from './theme';
import type { GraphCommit } from '../api';

export const TRUNK_NAMES = ['main', 'master', 'develop', 'trunk'];

export const ROW_STEP = 38; // vertical grid pitch between packed rows
export const TOP_PADDING = 96; // 2-row branch-label header + connector buffer
export const BOTTOM_PADDING = 24;
export const TRUNK_X = 700;
export const LANE_SPACING = 100; // x distance between adjacent lanes
export const SPINE_W = 3;
export const DOT_R = 6;
export const AVATAR_R = 11;
export const PIP_R = 6;
export const BUTTON_H = 30;
export const BUTTON_PAD_X = 12;
export const BUTTON_GAP = 16; // px between dot and button edge
export const BUTTON_RX = 6; // authz-style flat-edge rounded corners (not pill)
export const SIDE_MARGIN = 12;
export const CHAR_W = 7.3; // Fira Code 13px advance — measured empirically
export const VIEWBOX_W = 1400;
export const LABEL_H = 22;
// CI job dots: the combo-button's last segment, one dot per job.
export const JOB_DOT_R = 5;
export const JOB_DOT_GAP = 7;
export const MAX_JOB_DOTS = 4; // past this the segment shows "+n"

/** Width of the job-dot segment for `n` jobs; 0 when there are none. */
export function jobSegWidth(n: number): number {
  if (n <= 0) return 0;
  const dots = Math.min(n, MAX_JOB_DOTS);
  const more = n > MAX_JOB_DOTS ? `+${n - MAX_JOB_DOTS}`.length * CHAR_W + JOB_DOT_GAP : 0;
  return 2 * BUTTON_PAD_X + dots * 2 * JOB_DOT_R + (dots - 1) * JOB_DOT_GAP + more;
}
export const LABEL_TOP_MARGIN = 8; // gap above the topmost branch label

export interface Lane {
  branch: string;
  x: number;
  color: string;
  textColor: string;
}

export interface Row {
  commit: GraphCommit;
  lane: Lane;
  y: number;
  side: 'left' | 'right';
}

// One header block: the pills of every branch whose newest visible commit
// is `sha`, stacked top to bottom, and the dotted connector's endpoints.
export interface LabelGroup {
  sha: string;
  x: number;
  /** Top and bottom of the stacked pills. */
  top: number;
  bottom: number;
  /** Y of the tip commit's dot the connector runs down to. */
  tipY: number;
  pills: { lane: Lane; y: number }[];
}

export interface GraphLayout {
  lanes: Map<string, Lane>;
  rows: Row[];
  laneDots: Map<string, { y: number; sha: string }[]>;
  shaIndex: Map<string, { lane: Lane; y: number }>;
  labelGroups: LabelGroup[];
  fullBySha: Map<string, GraphCommit>;
  height: number;
}

export function primaryBranch(c: GraphCommit): string | undefined {
  if (!c.branches || c.branches.length === 0) return undefined;
  for (const t of TRUNK_NAMES) {
    if (c.branches.includes(t)) return t;
  }
  return c.branches[0];
}

export function avatarInitial(name: string, email: string): string {
  return ((name || email || '?').trim()[0] || '?').toUpperCase();
}

// Pick the row's "topline" metric — the headline number displayed in the
// combo-button. The first name in `preferred` wins; the caller orders that
// list so the experiment's position-0 objective leads it, with the user's
// pinned/typed metric names ahead of that when they asked for one.
// Otherwise prefer a metric whose name contains "score" (the increment idiom
// for the primary signal), else the first metric alphabetically so the
// choice is stable.
export function toplineMetric(
  metrics: Record<string, number> | undefined,
  preferred: string[],
): [string, number] | null {
  if (!metrics) return null;
  for (const n of preferred) {
    if (n in metrics) return [n, metrics[n]];
  }
  const names = Object.keys(metrics).sort();
  if (names.length === 0) return null;
  const score = names.find((n) => /score/i.test(n));
  const pick = score ?? names[0];
  return [pick, metrics[pick]];
}

// Width estimate for a branch label pill — Fira Code is monospace so
// char-count × advance + horizontal padding is reliable. Used both for
// rendering and for the layout pass's overlap detection.
export function pillWidth(branch: string): number {
  return Math.max(64, branch.length * 7.4 + 22);
}

// Topological lane assignment: walk commits oldest→newest. Trunk-named
// branches own the center lane. Side branches grab the slot immediately
// adjacent (alternating left/right of their parent lane's level) so fork
// connectors don't cross unrelated trunks. Slots are tracked by integer
// "level" — positive = right, negative = left, 0 = trunk.
// A branch's colors at x. Used for lanes and for branches that share a
// lane's commits without owning a lane (their pill still needs a color).
export function laneFor(branch: string, x: number, mode: ThemeMode): Lane {
  const color = colorForHue(hueOf(branch), mode);
  const textColor = preferWhiteFg(color) ? '#ffffff' : '#1a1a1a';
  return { branch, x, color, textColor };
}

export function computeLanes(
  commitsOldestFirst: GraphCommit[],
  mode: ThemeMode,
): Map<string, Lane> {
  const lanes = new Map<string, Lane>();
  // parent SHA → branch the parent lives on (its primary branch).
  const shaBranch = new Map<string, string>();
  // Track used levels so two branches never share a slot.
  const usedLevels = new Set<number>();
  let trunkAssigned = false;

  const claimLevel = (preferred: number): number => {
    if (!usedLevels.has(preferred)) {
      usedLevels.add(preferred);
      return preferred;
    }
    // Walk outward looking for the next free slot on the SAME side.
    const step = preferred >= 0 ? 1 : -1;
    let n = preferred + step;
    while (usedLevels.has(n)) n += step;
    usedLevels.add(n);
    return n;
  };

  const makeLane = (branch: string, level: number): Lane =>
    laneFor(branch, TRUNK_X + level * LANE_SPACING, mode);

  for (const c of commitsOldestFirst) {
    const br = primaryBranch(c);
    if (!br) continue;
    if (!lanes.has(br)) {
      if (!trunkAssigned && TRUNK_NAMES.includes(br)) {
        usedLevels.add(0);
        lanes.set(br, makeLane(br, 0));
        trunkAssigned = true;
      } else {
        // Anchor next to parent's lane if we know it, else flank the graph
        // from the outside-in alternating sides.
        const parentBr = c.parent_sha ? shaBranch.get(c.parent_sha) : undefined;
        const parentLane = parentBr ? lanes.get(parentBr) : undefined;
        const parentLevel = parentLane
          ? Math.round((parentLane.x - TRUNK_X) / LANE_SPACING)
          : 0;
        // Try +1 then -1 relative to parent so the first sibling fork goes
        // right and the second goes left, etc.
        const seedSide = lanes.size % 2 === 0 ? 1 : -1;
        const preferred = parentLevel + seedSide;
        const level = claimLevel(preferred);
        lanes.set(br, makeLane(br, level));
      }
    }
    shaBranch.set(c.sha, br);
  }
  if (!trunkAssigned && lanes.size > 0) {
    const first = lanes.values().next().value as Lane;
    lanes.set(first.branch, makeLane(first.branch, 0));
  }
  return lanes;
}

function emptyLayout(): GraphLayout {
  return {
    lanes: new Map(),
    rows: [],
    laneDots: new Map(),
    shaIndex: new Map(),
    labelGroups: [],
    fullBySha: new Map(),
    height: TOP_PADDING + BOTTOM_PADDING,
  };
}

// Lay out `visible` (the filtered commits) against `all` (the whole graph
// window, newest first), which supplies lanes and parent chains for commits
// the filter hid.
export function computeLayout(
  all: GraphCommit[],
  visible: GraphCommit[],
  mode: ThemeMode,
  branchTips: Record<string, string> = {},
  jobCounts: Map<string, number> = new Map(),
): GraphLayout {
  if (visible.length === 0) return emptyLayout();

  // Lane assignment uses the FULL commit set (so branches that exist only
  // because of metric-free commits still get a lane reserved for their
  // parent-of-metric-bearing commit).
  const fullOldestFirst = [...all].reverse();
  const lanes = computeLanes(fullOldestFirst, mode);
  // Full sha → commit lookup so the fork connector can walk the
  // single-parent chain past metric-free ancestors.
  const fullBySha = new Map<string, GraphCommit>();
  for (const c of all) fullBySha.set(c.sha, c);

  // Force-include fork-point commits. For each branch with visible commits,
  // walk parents past the oldest visible until we find a commit whose primary
  // branch differs — that's the fork point. If it's in the window but was
  // filtered out, splice it back in so the lane has a real anchor on the
  // parent lane instead of dangling.
  const shown = [...visible];
  const visibleSet = new Set(shown);
  const visibleByPB = new Map<string, GraphCommit[]>();
  for (const c of shown) {
    const pb = primaryBranch(c);
    if (!pb) continue;
    const arr = visibleByPB.get(pb) ?? [];
    arr.push(c);
    visibleByPB.set(pb, arr);
  }
  for (const [br, commits] of visibleByPB) {
    commits.sort((a, b) => stampOf(b) - stampOf(a));
    const oldest = commits[commits.length - 1];
    let parentSha = oldest.parent_sha;
    const seen = new Set<string>();
    while (parentSha && !seen.has(parentSha)) {
      seen.add(parentSha);
      const p = fullBySha.get(parentSha);
      if (!p) break;
      const pBr = primaryBranch(p);
      if (pBr && pBr !== br) {
        if (!visibleSet.has(p)) {
          shown.push(p);
          visibleSet.add(p);
        }
        break;
      }
      parentSha = p.parent_sha;
    }
  }

  const rows = packRows(shown, lanes, fullBySha, jobCounts);

  const laneDots = new Map<string, { y: number; sha: string }[]>();
  for (const r of rows) {
    const arr = laneDots.get(r.lane.branch) ?? [];
    arr.push({ y: r.y, sha: r.commit.sha });
    laneDots.set(r.lane.branch, arr);
  }
  const shaIndex = new Map<string, { lane: Lane; y: number }>();
  for (const r of rows) shaIndex.set(r.commit.sha, { lane: r.lane, y: r.y });

  const labelGroups = placeLabels(lanes, laneDots, shaIndex, branchTips, mode);

  // Rows were laid out from TOP_PADDING; move them so the newest row sits
  // just under the header band, whatever height the label tiers came to.
  const headerBottom = labelGroups.length
    ? Math.max(...labelGroups.map((g) => g.bottom))
    : LABEL_TOP_MARGIN;
  const shift = rows.length ? headerBottom + HEADER_ROW_GAP - rows[0].y : 0;
  if (shift !== 0) {
    for (const r of rows) r.y += shift;
    for (const dots of laneDots.values()) for (const d of dots) d.y += shift;
    for (const idx of shaIndex.values()) idx.y += shift;
    for (const g of labelGroups) g.tipY += shift;
  }

  const height = (rows[rows.length - 1]?.y ?? TOP_PADDING) + BOTTOM_PADDING;
  return { lanes, rows, laneDots, shaIndex, labelGroups, fullBySha, height };
}

// Horizontal extent [left, right] a row's dot, status pip and combo-button
// cover. An upper bound on what GraphRow draws: the subject at its 20-char
// cap, and the widest metric value the commit carries (plus room for a
// unit) standing in for whichever metric ends up headlined.
export function rowExtent(
  c: GraphCommit, laneX: number, side: 'left' | 'right', jobs = 0,
): [number, number] {
  const subject = (c.message || '').split('\n')[0].trim().slice(0, 20);
  let topChars = 0;
  for (const v of Object.values(c.metrics ?? {})) {
    topChars = Math.max(topChars, formatNum(v).length + 3);
  }
  const w = 2 * AVATAR_R + 8
    + (7 * CHAR_W + 2 * BUTTON_PAD_X)
    + (subject ? subject.length * CHAR_W + 2 * BUTTON_PAD_X : 0)
    + (topChars ? topChars * CHAR_W + 2 * BUTTON_PAD_X : 0)
    + jobSegWidth(jobs);
  const reach = DOT_R + BUTTON_GAP + w + 4;
  return side === 'right'
    ? [laneX - DOT_R - 2, Math.min(VIEWBOX_W - SIDE_MARGIN, laneX + reach)]
    : [Math.max(SIDE_MARGIN, laneX - reach), laneX + DOT_R + 2];
}

const EXTENT_PAD = 6; // horizontal room kept between rows sharing a slot

// Pack rows onto ROW_STEP slots, newest at the top. Commits are taken in
// topological order (every visible descendant first; ties go to the newest)
// and each lands in the highest slot below all its descendants where its
// extent clears every row already in that slot. Trunk-lane rows may flip to
// whichever side packs higher; side lanes keep the side away from the trunk.
function packRows(
  shown: GraphCommit[],
  lanes: Map<string, Lane>,
  fullBySha: Map<string, GraphCommit>,
  jobCounts: Map<string, number>,
): Row[] {
  const shownSet = new Set(shown.map((c) => c.sha));
  // Nearest shown ancestor, walking the parent chain past hidden commits.
  const parentOf = new Map<string, string>();
  for (const c of shown) {
    const seen = new Set<string>();
    let p = c.parent_sha;
    while (p && !seen.has(p)) {
      seen.add(p);
      if (shownSet.has(p)) { parentOf.set(c.sha, p); break; }
      p = fullBySha.get(p)?.parent_sha;
    }
  }
  const pending = new Map<string, number>(); // sha -> unplaced child count
  for (const c of shown) pending.set(c.sha, 0);
  for (const p of parentOf.values()) pending.set(p, (pending.get(p) ?? 0) + 1);
  const minSlot = new Map<string, number>();

  const slots: [number, number][][] = []; // slot -> occupied extents
  const fits = (slot: number, ext: [number, number]) =>
    !(slots[slot] ?? []).some(([l, r]) => !(r + EXTENT_PAD <= ext[0] || ext[1] + EXTENT_PAD <= l));
  const firstFit = (from: number, ext: [number, number]) => {
    let s = from;
    while (!fits(s, ext)) s++;
    return s;
  };

  const ready = shown.filter((c) => pending.get(c.sha) === 0);
  const rows: Row[] = [];
  while (ready.length > 0) {
    // Newest ready commit next, so the packing stays roughly chronological.
    let bi = 0;
    for (let i = 1; i < ready.length; i++) {
      if (stampOf(ready[i]) > stampOf(ready[bi])) bi = i;
    }
    const c = ready.splice(bi, 1)[0];
    const parent = parentOf.get(c.sha);
    const release = () => {
      if (!parent) return;
      const n = (pending.get(parent) ?? 1) - 1;
      pending.set(parent, n);
      if (n === 0) ready.push(shown.find((x) => x.sha === parent)!);
    };
    const br = primaryBranch(c);
    const lane = br ? lanes.get(br) : undefined;
    if (!lane) { release(); continue; }
    const from = minSlot.get(c.sha) ?? 0;
    const level = Math.round((lane.x - TRUNK_X) / LANE_SPACING);
    const sides: ('left' | 'right')[] = level < 0 ? ['left'] : level > 0 ? ['right'] : ['right', 'left'];
    let best: { slot: number; side: 'left' | 'right'; ext: [number, number] } | undefined;
    for (const side of sides) {
      const ext = rowExtent(c, lane.x, side, jobCounts.get(c.sha) ?? 0);
      const slot = firstFit(from, ext);
      if (!best || slot < best.slot) best = { slot, side, ext };
    }
    const { slot, side, ext } = best!;
    (slots[slot] ??= []).push(ext);
    rows.push({ commit: c, lane, y: TOP_PADDING + slot * ROW_STEP, side });
    if (parent) minSlot.set(parent, Math.max(minSlot.get(parent) ?? 0, slot + 1));
    release();
  }
  // Newest-first by position: laneDots, label tips and the header shift all
  // read rows[0] as the top row.
  rows.sort((a, b) => a.y - b.y || stampOf(b.commit) - stampOf(a.commit));
  return rows;
}

function stampOf(c: GraphCommit): number {
  if (!c.authored_at) return 0;
  const t = Date.parse(c.authored_at);
  return Number.isFinite(t) ? t : 0;
}

const LABEL_STACK_GAP = 6; // vertical room between stacked pills and tiers
const LABEL_X_PAD = 8; // horizontal room before two blocks count as overlapping
const HEADER_ROW_GAP = 34; // header band bottom -> newest row's center

// Header label placement. Every branch is labelled once, at its newest
// visible commit: a lane's branch at the lane's newest dot, and any other
// branch from branch_tips whose tip commit is drawn. Branches sharing a tip
// commit form one block of stacked pills. Blocks are placed left to right
// on a skyline: each sits on the highest block it overlaps horizontally, so
// non-overlapping blocks share the bottom tier and the band stays short.
// Y values come out relative to the band; computeLayout positions the rows
// below it.
function placeLabels(
  lanes: Map<string, Lane>,
  laneDots: Map<string, { y: number; sha: string }[]>,
  shaIndex: Map<string, { lane: Lane; y: number }>,
  branchTips: Record<string, string>,
  mode: ThemeMode,
): LabelGroup[] {
  const bySha = new Map<string, string[]>();
  const add = (sha: string, branch: string) => {
    const list = bySha.get(sha) ?? [];
    if (!list.includes(branch)) list.push(branch);
    bySha.set(sha, list);
  };
  for (const lane of lanes.values()) {
    const tip = laneDots.get(lane.branch)?.[0];
    if (tip) add(tip.sha, lane.branch);
  }
  for (const [branch, sha] of Object.entries(branchTips).sort(([a], [b]) => a.localeCompare(b))) {
    if (lanes.has(branch) || !shaIndex.has(sha)) continue;
    add(sha, branch);
  }

  interface Block { group: LabelGroup; left: number; right: number; base: number; h: number }
  const blocks: Block[] = [];
  for (const [sha, branches] of bySha) {
    const at = shaIndex.get(sha)!;
    // The lane's own branch leads its block; the rest follow by name.
    branches.sort((a, b) =>
      Number(b === at.lane.branch) - Number(a === at.lane.branch) || a.localeCompare(b));
    const w = Math.max(...branches.map(pillWidth));
    const h = branches.length * LABEL_H + (branches.length - 1) * LABEL_STACK_GAP;
    blocks.push({
      group: {
        sha, x: at.lane.x, top: 0, bottom: 0, tipY: at.y,
        pills: branches.map((b) => ({ lane: lanes.get(b) ?? laneFor(b, at.lane.x, mode), y: 0 })),
      },
      left: at.lane.x - w / 2,
      right: at.lane.x + w / 2,
      base: 0,
      h,
    });
  }
  blocks.sort((a, b) => a.left - b.left || a.group.tipY - b.group.tipY);
  const placed: Block[] = [];
  for (const blk of blocks) {
    for (const p of placed) {
      const overlaps = !(p.right + LABEL_X_PAD < blk.left || p.left > blk.right + LABEL_X_PAD);
      if (overlaps) blk.base = Math.max(blk.base, p.base + p.h + LABEL_STACK_GAP);
    }
    placed.push(blk);
  }
  const bandH = placed.reduce((m, b) => Math.max(m, b.base + b.h), 0);
  const bandBottom = LABEL_TOP_MARGIN + bandH;
  for (const blk of placed) {
    const g = blk.group;
    g.bottom = bandBottom - blk.base;
    g.top = g.bottom - blk.h;
    g.pills.forEach((pill, i) => { pill.y = g.top + i * (LABEL_H + LABEL_STACK_GAP); });
  }
  return placed.map((b) => b.group);
}
